import React, { useState } from 'react';
import { useTcgSets } from '../../hooks/useTcgSets';

const SUPERTYPES = ['Pok\u00e9mon', 'Trainer', 'Energy'];

export default function TcgCardFilters({ filters, onFilterChange }) {
  const [expanded, setExpanded] = useState(false);
  const { sets } = useTcgSets();
  const [local, setLocal] = useState({
    name: filters.name || '',
    set_id: filters.set_id || '',
    artist: filters.artist || '',
    rarity: filters.rarity || '',
    supertype: filters.supertype || '',
    language: filters.language || 'en',
  });

  const handleChange = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  // When language changes, clear set selection if it doesn't match
  const handleLanguageChange = (newLang) => {
    setLocal((prev) => {
      const updated = { ...prev, language: newLang };
      if (prev.set_id && newLang) {
        const matchedSet = sets.find((s) => s.id === prev.set_id);
        if (matchedSet && matchedSet.language !== newLang) {
          updated.set_id = '';
        }
      }
      return updated;
    });
  };

  // When set changes, auto-detect language
  const handleSetChange = (newSetId) => {
    setLocal((prev) => {
      const updated = { ...prev, set_id: newSetId };
      if (newSetId) {
        const matchedSet = sets.find((s) => s.id === newSetId);
        if (matchedSet && matchedSet.language === 'ja' && prev.language !== 'ja') {
          updated.language = 'ja';
        } else if (matchedSet && matchedSet.language === 'en' && prev.language !== 'en') {
          updated.language = 'en';
        }
      }
      return updated;
    });
  };

  const handleApply = () => {
    const cleaned = {};
    Object.entries(local).forEach(([key, value]) => {
      if (value !== '' && value !== null) cleaned[key] = value;
    });
    onFilterChange(cleaned);
  };

  const handleClear = () => {
    const empty = { name: '', set_id: '', artist: '', rarity: '', supertype: '', language: 'en' };
    setLocal(empty);
    onFilterChange({ language: 'en' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApply();
  };

  // Filter sets by selected language for the dropdown
  const filteredSets = local.language
    ? sets.filter((s) => s.language === local.language)
    : sets;

  // Group filtered sets by series for the dropdown
  const seriesGroups = {};
  filteredSets.forEach((s) => {
    if (!seriesGroups[s.series]) seriesGroups[s.series] = [];
    seriesGroups[s.series].push(s);
  });

  return (
    <div className="card-filters">
      <div className="filters-header">
        <button
          className="btn btn-sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      {expanded && (
        <div className="filters-body" onKeyDown={handleKeyDown}>
          <div className="filter-row">
            <div className="filter-field">
              <label>Card Name</label>
              <input
                type="text"
                value={local.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Search by name..."
              />
            </div>
            <div className="filter-field">
              <label>Language</label>
              <select
                value={local.language}
                onChange={(e) => handleLanguageChange(e.target.value)}
              >
                <option value="">All</option>
                <option value="en">English / French</option>
                <option value="ja">Japanese</option>
              </select>
            </div>
            <div className="filter-field">
              <label>Expansion / Set</label>
              <select
                value={local.set_id}
                onChange={(e) => handleSetChange(e.target.value)}
              >
                <option value="">All Sets</option>
                {Object.entries(seriesGroups).map(([series, seriesSets]) => (
                  <optgroup key={series} label={series}>
                    {seriesSets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.total || s.printed_total} cards)
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="filter-field">
              <label>Artist / Illustrator</label>
              <input
                type="text"
                value={local.artist}
                onChange={(e) => handleChange('artist', e.target.value)}
                placeholder="e.g. Mitsuhiro Arita"
              />
            </div>
            <div className="filter-field">
              <label>Rarity</label>
              <input
                type="text"
                value={local.rarity}
                onChange={(e) => handleChange('rarity', e.target.value)}
                placeholder="e.g. Rare Holo"
              />
            </div>
            <div className="filter-field">
              <label>Supertype</label>
              <select
                value={local.supertype}
                onChange={(e) => handleChange('supertype', e.target.value)}
              >
                <option value="">All</option>
                {SUPERTYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-primary" onClick={handleApply}>Apply</button>
            <button className="btn" onClick={handleClear}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}
